import { DASHBOARD_FONT_FAMILY } from 'App/dashboards/_utils/dashboardFont'
import type {
  TooltipFormatterContextObject,
  TooltipOptions,
  XAxisPlotLinesOptions,
} from 'highcharts/highstock'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { colors } from 'settings/colors'
import { alphabeticalSort } from 'utils/alphabeticalSort'
import { escapeHtml } from 'utils/escapeHtml'

export type TTooltipPlotLine = XAxisPlotLinesOptions & {
  tooltipDisplay?: (ctx: TooltipFormatterContextObject) => string
}

export const pestTooltipStyle: TooltipOptions = {
  shared: true,
  // highstock defaults tooltip.split to true, which overrides `shared` and renders one label per
  // series instead of a single shared box — explicitly off so our shared formatter is used.
  split: false,
  useHTML: true,
  backgroundColor: 'transparent',
  borderWidth: 0,
  followPointer: true,
  hideDelay: 0,
  outside: false,
  padding: 0,
  shadow: false,
  style: {
    fontFamily: DASHBOARD_FONT_FAMILY,
    fontSize: '16px',
  },
}

const formatPointValue = (y: number | null | undefined, decimals: number | undefined): string => {
  if (y === null || y === undefined) return '-'

  return typeof decimals === 'number' ? y.toFixed(decimals) : String(y)
}

// Block summary shared by the plot-line tooltips (applied sprays, bloom, hull split) that list the
// blocks an event applies to:
//   - 1 block  → just the block name (no count prefix, which reads awkwardly)
//   - 2 blocks → "2 blocks: A, B"
//   - 3+       → "N blocks: A, B, +N more" (first two names, the rest collapsed)
// Empty string when there are no blocks.
export const formatBlockSummary = (blockNames: string[]): string => {
  const count = blockNames.length

  if (count === 0) return ''

  // Escape names — they're customer-controlled and land in the useHTML tooltip.
  const escaped = blockNames.map(escapeHtml)

  if (count === 1) return escaped[0]

  let names = escaped.join(', ')

  if (count > 2) {
    names = `${escaped.slice(0, 2).join(', ')}, ${translate.phrases.banyanApp('+{{count}} more', {
      count: String(count - 2),
    })}`
  }

  return `<b>${translate.phrases.banyanApp('{{count}} blocks', { count: String(count) })}:</b> ${names}`
}

// Spray plot lines sit at a day boundary that may be offset from the (UTC-midnight) data points by
// the local timezone, so match within half a day rather than on an exact timestamp.
const PLOT_LINE_MATCH_MS = 12 * 60 * 60 * 1000

// Rows for any plot line with a `tooltipDisplay` whose date is at the hovered point (applied
// sprays). Reads the live plot lines off the chart's x-axis, the same approach the StackedChart
// uses, so toggling the legend (which adds/removes the lines) keeps the tooltip in sync.
const buildPlotLineTooltipRows = (ctx: TooltipFormatterContextObject): string => {
  const x = Number(ctx.x)
  const xAxes = ctx.points?.[0]?.series?.chart?.xAxis ?? []

  const plotLines = xAxes.flatMap(
    (axis) =>
      (axis as unknown as { plotLinesAndBands?: { options: TTooltipPlotLine }[] }).plotLinesAndBands ?? [],
  )

  return plotLines
    .filter(
      (pl) =>
        typeof pl.options.tooltipDisplay === 'function' &&
        pl.options.value !== null &&
        pl.options.value !== undefined &&
        Math.abs(x - Number(pl.options.value)) <= PLOT_LINE_MATCH_MS,
    )
    .map((pl) => {
      const { color, width = 2, dashStyle, tooltipDisplay } = pl.options
      const lineStyle = dashStyle && dashStyle !== 'Solid' ? 'dotted' : 'solid'

      return `
        <tr>
          <td>
            <div style="border-top: ${width}px ${lineStyle} ${String(
              color,
            )}; width: 24px; height: 0; margin: 0 7px 2px 0;"></div>
          </td>
          <td colspan="2">${tooltipDisplay?.(ctx) ?? ''}</td>
        </tr>`
    })
    .join('')
}

// One tooltip for every series at the hovered date. Each row gets the series' colour swatch, name,
// and value (with the series' own decimals/suffix). The header appends "(Forecast)" once past the
// degree day forecast start, matching the StackedChart behaviour.
export const buildPestTooltipFormatter = ({
  firstForecastMs,
  timezone,
}: {
  firstForecastMs: number | null
  timezone: string
}) =>
  function (this: TooltipFormatterContextObject): string {
    const points = this.points ?? []

    if (points.length === 0) return ''

    const x = Number(this.x)
    const isForecast = firstForecastMs !== null && x >= firstForecastMs
    // Localized via translate.dates (like the Hub map-panel chart tooltips), not raw moment.format.
    const dateLabel = translate.dates.format(moment.tz(x, timezone), 'MMM D, YYYY')
    const forecastTag = isForecast ? ` (${translate.phrases.banyanApp('Forecast')})` : ''

    const rows = [...points]
      // Sort rows by series name so the degree-day station lines read alphabetically, matching the
      // legend — Highcharts' shared-tooltip point order isn't guaranteed. Use the app-wide
      // alphabeticalSort (Intl.Collator numeric + ignorePunctuation): station names can carry stray
      // internal whitespace (e.g. "CRO  35B   WS"), which a plain localeCompare would mis-order by
      // sorting the extra spaces ahead of digits.
      .sort((a, b) => alphabeticalSort(a.series.name ?? '', b.series.name ?? ''))
      .map((p) => {
        const seriesTooltip = p.series.userOptions.tooltip
        const value = formatPointValue(p.y, seriesTooltip?.valueDecimals)
        const suffix = value === '-' ? '' : (seriesTooltip?.valueSuffix ?? '')

        return `
          <tr>
            <td>
              <div style="background:${String(
                p.series.color,
              )}; min-width: 14px; min-height: 5px; margin: 0 7px 0 0;"></div>
            </td>
            <td style="text-align: left">${escapeHtml(String(p.series.name ?? ''))}:</td>
            <td style="text-align: right; padding: 0 0 0 5px;"><b>${value}${suffix}</b></td>
          </tr>`
      })
      .join('')

    // Opaque HTML box (replaces Highcharts' SVG box) so the tooltip stays solid over the useHTML
    // legend when it overlaps. Padding/radius/shadow match the previous Highcharts box.
    return (
      `<div style="background: ${colors.white}; border-radius: 8px; padding: 12px; box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);">` +
      `<strong>${dateLabel}${forecastTag}</strong>` +
      `<table>${rows}${buildPlotLineTooltipRows(this)}</table>` +
      '</div>'
    )
  }
